import { Fragment, useEffect, useMemo } from 'react'
import { Textfield, Grid, Cell, Checkbox } from 'react-mdl'
import { useObserver } from 'mobxReactLite'
import useTimeAndDateStore from './useTimeAndDateStore'
import { formatDateFromDb, onKeyActivate } from '@utils'

const { FlatDatepicker, I18n } = window

const TimeAndTravel = ({
  name,
  value,
  start_at,
  end_at,
  no_hours,
  question,
  disabled,
  activityStartDate,
}) => {
  const dateFormat = I18n?.t?.('date.formats.form_date_hint') || 'MM/DD/YYYY'
  const noHoursLabel =
    I18n?.t?.('events.views.instructor_results.no_hours_to_submit') || 'No hours to submit'
  const {
    time_code: timeCode,
    collect_time_log: collectTimeLog,
    question: label,
    required,
  } = question

  const activityDate = useMemo(() => {
    if (!activityStartDate) {
      const today = new Date()
      today.setHours(0, 0, 0, 0)
      return today
    }

    const dateStr = activityStartDate.split('T')[0]
    const [year, month, day] = dateStr.split('-').map(Number)
    const date = new Date(year, month - 1, day)
    date.setHours(0, 0, 0, 0)

    return date
  }, [activityStartDate])

  const store = useTimeAndDateStore({
    startAt: start_at,
    endAt: end_at,
    detailValue: value,
    computeValue: collectTimeLog,
    noHours: no_hours,
  })

  useEffect(() => {
    if (!store.date && activityDate) {
      store.setDate(activityDate)
    }
  }, [activityDate, store])

  const timeFieldOptions = useMemo(
    () => ({
      noCalendar: true,
      enableTime: true,
      minuteIncrement: 15,
    }),
    []
  )

  const handleDateChange = (dates) => {
    if (dates && dates.length > 0) {
      const normalized = new Date(dates[0])
      normalized.setHours(0, 0, 0, 0)
      store.setDate(normalized)
    }
  }

  const handleStartTimeChange = (dates) => {
    if (dates && dates.length > 0) {
      store.setStartAt(dates[0])
      store.setEndAt(dates[0])
    }
  }

  const handleEndTimeChange = (dates) => {
    if (dates && dates.length > 0) {
      store.setEndAt(dates[0])
    }
  }

  const handleNoHoursChange = () => {
    store.setNoHours(!store.noHours)
  }

  const handleValueChange = (e) => {
    store.setValue(e.target.value)
  }

  return useObserver(() => (
    <fieldset>
      <Grid className="mdl-grid--nested">
        <Cell col={12}>
          <legend className="text--darkblue">{label}</legend>
        </Cell>

        {collectTimeLog ? (
          <input type="hidden" name={`${name}[value]`} value={store.computedValue || ''} />
        ) : (
          <input type="hidden" name={`${name}[value]`} value={store.detailValue || ''} />
        )}
        <input type="hidden" name={`${name}[start_at]`} value={store.startAt || ''} />
        <input type="hidden" name={`${name}[end_at]`} value={store.endAt || ''} />
        <input type="hidden" name={`${name}[no_hours]`} value={store.noHours} />

        <Cell col={4}>
          <FlatDatepicker
            label="Date"
            value={store.noHours ? '' : formatDateFromDb(store.date || activityDate, dateFormat)}
            placeholder={store.noHours ? '' : dateFormat}
            dateFormat={dateFormat}
            onChange={handleDateChange}
            options={{
              allowInput: true,
              enable: [activityDate],
            }}
            required={required}
            disabled={disabled || store.noHours}
          />
        </Cell>

        {collectTimeLog ? (
          <Fragment>
            <Cell col={2}>
              <FlatDatepicker
                placeholder={store.noHours ? '' : '0:00'}
                value={store.noHours ? '' : store.startAt}
                label={`${required ? '* ' : ''}Start Time`}
                dateFormat="LT"
                onChange={handleStartTimeChange}
                forceUpdate={store.timestamp}
                options={timeFieldOptions}
                disabled={disabled || store.noHours}
              />
            </Cell>
            <Cell col={2}>
              <FlatDatepicker
                placeholder={store.noHours ? '' : '0:00'}
                value={store.noHours ? '' : store.endAt}
                label={`${required ? '* ' : ''}End Time`}
                dateFormat="LT"
                onChange={handleEndTimeChange}
                forceUpdate={store.timestamp}
                options={timeFieldOptions}
                disabled={disabled || store.noHours}
              />
            </Cell>
            <Cell col={4}>
              <Textfield
                readOnly
                floatingLabel
                className="always-floating-label"
                type="number"
                label={label}
                value={store.computedValue}
                error={store.valueError}
                disabled={disabled || store.noHours}
              />
            </Cell>
            <Cell col={12}>
              <Checkbox
                label={noHoursLabel}
                className="mb-10"
                checked={store.noHours}
                onKeyDown={onKeyActivate('click')}
                disabled={disabled}
                onChange={handleNoHoursChange}
              />
            </Cell>
          </Fragment>
        ) : (
          <Cell col={4}>
            {timeCode === 'mileage' ? (
              <Textfield
                disabled={disabled}
                floatingLabel
                type="number"
                label={required ? `* ${label}` : label}
                required={required}
                value={store.detailValue}
                onChange={handleValueChange}
                error={store.valueError}
                step="1"
                min="1"
                max="999"
              />
            ) : (
              <Textfield
                disabled={disabled}
                floatingLabel
                type="number"
                label={required ? `* ${label}` : label}
                required={required}
                value={store.detailValue}
                onChange={handleValueChange}
                error={store.valueError}
                step="0.01"
                min="0"
                max="24"
              />
            )}
          </Cell>
        )}
      </Grid>
    </fieldset>
  ))
}

export default TimeAndTravel
